import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Trash2, AlertTriangle } from "lucide-react";

export const DELETION_REASONS = [
  "Duplicate MR",
  "Wrong entry",
  "Cancelled order",
  "Test record",
  "Other",
];

/**
 * Hardened "Delete Shipment" confirmation dialog.
 * Mandates: reason category, optional notes (or required for "Other"),
 * and the user typing 'DELETE' verbatim before the confirm button enables.
 */
export default function DeleteShipmentDialog({
  open,
  onOpenChange,
  // For single-MR delete: pass `mrNumber`. For bulk: pass `bulkCount`.
  mrNumber,
  bulkCount,
  busy,
  onConfirm, // (reason, notes, confirm) => Promise
  testIdPrefix = "delete",
}) {
  const [reason, setReason] = useState(DELETION_REASONS[0]);
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState("");

  // Reset when dialog re-opens
  useEffect(() => {
    if (open) {
      setReason(DELETION_REASONS[0]);
      setNotes("");
      setConfirm("");
    }
  }, [open]);

  const isBulk = !!bulkCount && bulkCount > 0;
  const otherRequired = reason === "Other";
  const notesOK = otherRequired ? notes.trim().length >= 3 : true;
  const confirmOK = confirm.trim().toUpperCase() === "DELETE";
  const canSubmit = !busy && notesOK && confirmOK;

  const submit = () => {
    if (!canSubmit) return;
    onConfirm(reason, notes.trim(), confirm.trim().toUpperCase());
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={`${testIdPrefix}-modal`} className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-rose-700" />
            {isBulk ? `Delete ${bulkCount} shipment${bulkCount === 1 ? "" : "s"}?` : `Delete shipment ${mrNumber}?`}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-700">
            <span className="font-bold text-rose-700 block mb-1">
              This action cannot be undone.
            </span>
            This will permanently delete the {isBulk ? "selected shipments" : "entire shipment record"} and all
            their history, comments, and notifications. The deletion is recorded in the audit log.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-1">
          {!isBulk && (
            <div className="rg-card p-2.5 bg-slate-100 border-2 border-slate-300 text-xs">
              <span className="font-bold uppercase tracking-wider text-slate-500 mr-2">MR</span>
              <span className="font-mono text-base font-bold text-slate-900" data-testid={`${testIdPrefix}-mr`}>
                {mrNumber}
              </span>
            </div>
          )}

          {/* Reason dropdown */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
              Deletion reason *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid={`${testIdPrefix}-reason-select`}
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-sm bg-white focus:border-rose-700 outline-none"
            >
              {DELETION_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Notes / detail (required if Other) */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
              Notes {otherRequired ? <span className="text-rose-700">*</span> : <span className="text-slate-400">(optional)</span>}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid={`${testIdPrefix}-notes-input`}
              rows={2}
              placeholder={otherRequired ? "Required when reason is Other (min 3 chars)" : "Add any extra context for the audit log"}
              className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:border-rose-700 outline-none"
            />
          </div>

          {/* DELETE typing confirmation */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
              Type <span className="font-mono text-rose-700 bg-rose-50 px-1 rounded-sm">DELETE</span> to confirm *
            </label>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid={`${testIdPrefix}-confirm-input`}
              placeholder="DELETE"
              autoComplete="off"
              spellCheck={false}
              className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 font-mono font-bold tracking-widest bg-white focus:border-rose-700 outline-none uppercase"
            />
            <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Confirms you understand this is permanent.
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testIdPrefix}-cancel-btn`} disabled={busy}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={submit}
            disabled={!canSubmit}
            data-testid={`${testIdPrefix}-confirm-btn`}
            className="bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Deleting…" : isBulk ? `Permanently delete ${bulkCount}` : "Permanently delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
